<template>
    <div ref="container" :class="{ ...className }">
        <p v-if="form.productTitle" class="s-form-title">
            <a-link
                v-if="form.productLink"
                :to="form.productLink"
                :text="form.productTitle"
            />
            <span v-else v-html="form.productTitle" />
        </p>

        <div class="s-form-content">
            <div v-if="isLoading" class="s-form-container__loading">
                <el-spinner :size="48" color="brand-secondary" />
                <p v-html="loadingMessage" />
            </div>
            <form-support-confirmation
                v-if="isActive('support')"
                :content="form.support"
                :form="userData"
            />
            <form-schedule
                v-if="isActive('schedule')"
                :content="formScheduleContent"
            />
            <form-constructor
                v-if="isActive('registration') || Boolean(isActive('upsell') && [47424, 47425].includes(form.version))"
                :user-data="userData"
                :settings-form="settingsForm"
                :content="formRegistrationContent"
                :use-phone-input="form.registration.usePhoneInput"
                :mailgun-error="mailgunError"
                :error-message-a-p-i="errorMessageAPI"
                :has-animate="hasAnimate"
                @loading="isLoading = $event"
                @submit="isSubmittedRegistration"
                @submit-error="onSubmitError"
                @change-step="changeStep($event)"
                @resize="$emit('resize')"
                @hideLogin="hideLogin"
                @extra-button-click="$emit('extra-button-click', $event)"
            />
            <form-login
                v-if="isActive('login')"
                :content="form.login"
                :form-step="formStep"
                @error="showForm('error')"
                @loading="isLoading = $event"
                @submit="getUserData()"
            />
            <form-constructor
                v-if="isActive('update')"
                :content="formUpdateContent"
                :settings-form="settingsForm"
                :use-phone-input="formUpdateContent.usePhoneInput"
                @showForm="showForm"
                @submit="isSubmittedUpdate"
                @resize="$emit('resize')"
            />
            <form-upsell
                v-if="isActive('upsell')"
                :content="form.upsell"
                @showNotification="showNotification($event)"
                @submit="isSubmittedUpsell()"
            />
            <form-error v-if="isActive('error')" :content="errorContent" @submit="showForm(errorContent.activeForm)" />
            <form-success
                v-if="isActive('success')"
                :form="form.success"
                :user-data="userData"
                @onClickButtonSuccess="onClickButtonSuccess"
                @backToForm="showForm"
            />
            <form-notification v-if="isActive('notification')" :content="notification" @backToForm="showForm" />
            <label v-if="hasRecaptchaInvisible" class="sr-only" for="g-recaptcha-response">Google Recaptcha</label>
            <div v-if="hasRecaptchaInvisible" ref="recaptcha" class="g-recaptcha" />
        </div>
        <s-exit-banner
            v-if="form?.exitBanner"
            ref="exitBanner"
            :content="form.exitBanner"
            :enabled="activeForm === 'registration' && !isLoading"
        />
    </div>
</template>

<script>
import { get, mapValues, isString } from 'lodash-es';
import { mapState, mapActions } from 'vuex';
import ALink from '@core/components/link/link.vue';
import { Loading } from '@core/components/loading';
import ElSpinner from '@core/components/spinner/spinner.vue';
import cleverbridge from '@core/mixins/cleverbridge.js';
import currency from '@core/mixins/currency.js';
import form from '@core/mixins/form';
import recaptcha from '@core/mixins/recaptcha.js';
import { LOCALE_DEFAULT } from '@model/const/locales.ts';
import commonUtils from '@utils/common';
import utils from '@utils/form.js';
import { sleep } from '@utils/promise.ts';

import { getDB } from '../../db.js';
import * as twoFa from '../../two-fa.ts';
import SExitBanner from '../exit-banner.vue';

import formConstructor from './form-constructor.vue';
import formError from './form-error.vue';
import formLogin from './form-login.vue';
import formNotification from './form-notification.vue';
import formSchedule from './form-schedule.vue';
import formSuccess from './form-success.vue';
import formSupportConfirmation from './form-support-confirmation.vue';
import formUpsell from './form-upsell.vue';

const EVENT_FORM_SUCCESS = 'form-submit-success';
const EVENT_FORM_ERROR = 'form-submit-error';

export default {
    name: 'SFormContainer',

    components: {
        ALink,
        SExitBanner,
        formConstructor,
        formError,
        formLogin,
        formNotification,
        formSchedule,
        formSupportConfirmation,
        formSuccess,
        formUpsell,
        ElSpinner,
    },

    directives: {
        loading: Loading.directive,
    },

    mixins: [form, recaptcha, currency, cleverbridge],

    props: {
        form: {
            type: Object,
            default: null,
        },

        isActiveSuccess: {
            type: Boolean,
            default: false,
        },

        hasAnimate: {
            type: Boolean,
            default: false,
        },
    },

    emits: ['onClickButtonSuccess', 'submit', 'resize', 'changeStep', 'activeFormName', 'extra-button-click'],

    data: () => ({
        ck: '',
        activeForm: '',
        userData: {},
        settingsForm: {},
        isLoading: true,
        serverErrors: [],
        notification: {},
        backTo: 'registration',
        FORMS: ['registration', 'login', 'update', 'previous', 'start'],
        content: null,
        errorContent: null,
        mailgunError: {},
        redirectLink: '',
        redirectSegmented: '',
        segmentRules: null,
        response: {},
        sendLeadTo: '',
        formInstance: null,
        formEventContext: '',
        errorMessageAPI: '',
        formStep: null,
        previousFormStep: null,
        replaceExtraButtons: null,
        loadingMessage: '',
        formRegistrationFields: [],
        isShaking: false,
        session2FAResolved: false,
    }),

    computed: {
        ...mapState({
            location: (state) => state.geolocation?.location || null,
            copyright: (state) => get(state.slices.items, 's-global-footer.copyright', ''),
            legalLinks: (state) => get(state.slices.items, 's-global-footer.legalLinks', []),
            countriesData: (state) => state.countries?.items || [],
        }),

        className() {
            return {
                's-form-container': this.form,
                [`s-form-container-version-${this.form.version}`]: this.form.version,
                's-form-container--shake': this.isShaking,
                [`s-form-step-${this.formStep + 1}`]: this.formHasSteps,
            };
        },

        hasFormID() {
            return Boolean(this.form?.flow?.marketo?.form_id);
        },

        formScheduleContent() {
            const conditions = this.form.schedule?.conditions || [];
            if (!conditions.length) return null;

            const userLocation = this.userData?.country || this.location?.data?.country?.code;
            const userCompanyType = this.userData?.company_type;

            for (const condition of conditions) {
                const matchedItem = condition.list?.find((item) => {
                    const countryMatch = !item.countries || item.countries.includes(userLocation);
                    const companyTypeMatch = !item.company_type || item.company_type === userCompanyType;
                    return countryMatch && companyTypeMatch;
                });

                if (matchedItem) {
                    return {
                        ...condition.content,
                        link: matchedItem.link,
                    };
                }
            }

            return null;
        },

        hasSuccessPage() {
            return get(this, 'form.flow.successPage', false);
        },

        submitDelay() {
            return get(this, 'form.settings.submitDelay', 0);
        },

        isContactsRenewals() {
            return Boolean(this.form?.flow?.sendContactsRenewals);
        },

        hasNotification() {
            return get(this, 'form.flow.notification', false);
        },

        formRegistrationContent() {
            return {
                ...this.form.registration,
                sliceID: this.form.sliceID,
                version: this.form.version,
                isFormExpressSignup: Boolean(this.form?.flow?.isFormExpressSignup),
                isFormEvents: Boolean(this.form?.settings?.isFormEvents),
                hasHoneypot: Boolean(this.form?.settings?.hasHoneypot),
                hasAsterisks: this.form?.settings?.hasAsterisks,
                is46514VariantB: this.form?.settings?.is46514VariantB,
                isCloudConsoleLoginFlowEnabled: this.isCloudConsoleLoginFlowEnabled,
                fields: this.formRegistrationFields,
                ...this.formVariation,
            };
        },

        formHasSteps() {
            return this.form?.registration?.steps?.length;
        },
        skipSendLead() {
            return Boolean(this.form?.registration?.skipSendLead);
        },
        formVariation() {
            if (!this.formHasSteps) return {};
            const currentStep = this.form.registration?.steps?.[this.formStep] || {};
            return this.replaceExtraButtons ? { ...currentStep, extraButtons: this.replaceExtraButtons } : currentStep;
        },

        finalEventsGA() {
            const isLastStepIndex = this.formStep === this.form.registration?.steps?.length - 1;
            const isLastStep = this.formHasSteps && isLastStepIndex;
            if (isLastStep && this.formVariation?.sendEventsGA?.length) {
                return this.formVariation.sendEventsGA;
            }
            return this.form?.flow?.sendEventsGA || [];
        },

        formUpdateContent() {
            return {
                ...this.settingsFormUpdate,
                subtitles: [this.accountData?.email],
                hasAsterisks: this.form?.settings?.hasAsterisks,
                ...this.form.update,
            };
        },

        needToRegisterAccount() {
            return this.form?.flow?.registerAccount || false;
        },

        isResourceCenter() {
            return Boolean(this.form?.flow?.isResourceCenter);
        },

        listProductID() {
            return this.form?.flow?.registerProducts || [762];
        },

        registrationFields() {
            return this.applyExtraFields(this.form?.registration?.fields || []);
        },

        formFields() {
            const steps = this.form?.registration?.steps || [];
            const fields = this.form?.registration?.fields || steps.flatMap((item) => item.fields || []) || [];
            return this.applyExtraFields(fields);
        },
    },

    watch: {
        form() {
            this.formRegistrationFields = this.registrationFields;
            this.initFlowSettings();
        },
        accountData(data) {
            if (this.activeForm === 'update' && !data) {
                this.showForm('login');
            }
        },
    },

    beforeUnmount() {
        this.$refs.container.removeEventListener('click', this.handleClick);
    },

    beforeMount() {
        if (this.formHasSteps) this.formStep = 0;
        this.activeForm = this.isActiveSuccess ? 'success' : '';
        this.loadingMessage = this.form?.registration?.loading || 'Loading';
        this.formRegistrationFields = this.registrationFields;
    },

    mounted() {
        this.initFlowSettings();
        this.$refs.container.addEventListener('click', this.handleClick);

        this.ck = utils.getCookies(utils.MRKTO_COOKIE_NAME) || utils.createMarketoToken();

        this.init();
    },

    async serverPrefetch() {
        if (!import.meta.env.SSR) return;

        if (this.form?.registration) {
            const locale = this.$store.state.geolocation.finalLocale || LOCALE_DEFAULT;
            await this.$store.dispatch('slices/getSyncedData', { slice: 's-data-centers', locale });
        }
    },

    methods: {
        ...mapActions({
            accountUpdate: 'trial/accountUpdate',
            accountAgreement: 'trial/accountAgreement',
            accountLogin: 'trial/accountLogin',
            accountRegister: 'trial/accountRegister',
            accountTrial: 'trial/accountTrial',
            sendLead: 'trial/sendLead',
            isEmailValid: 'trial/isEmailValid',
            generateSessionURL: 'cleverbridge/get',
            getGeoLocation: 'geolocation/getGeoLocation',
        }),

        initFlowSettings() {
            this.sendLeadTo = this.form?.flow?.sendLeadTo || '';
            this.redirectLink = this.form?.flow?.redirectLink || '';
            this.redirectSegmented = this.form?.flow?.redirectSegmented || '';
            this.segmentRules = this.form?.flow?.segmentRules || null;
        },

        handleClick(e) {
            const str = e.target?.hash || e.target?.parentElement?.hash || e.target?.closest('a')?.hash || '';
            const hash = str.replace('#', '');
            if (hash && this.FORMS.includes(hash)) {
                e.preventDefault();
                if (hash === 'start') {
                    this.changeStep(0);
                    return;
                }
                if (hash === 'previous') {
                    if (this.activeForm === 'login') {
                        this.showForm('registration');
                        return;
                    }
                    this.changeStep(this.previousFormStep ?? (this.formStep - 1));
                    return;
                }
                if (hash === 'registration') {
                    this.userData = {};
                    this.changeStep(this.form.registration?.step || 0);
                    this.settingsForm = {};
                    this.errorMessageAPI = '';
                }
                this.showForm(hash);
                this.$router?.replace({path: this.$route.path});
            }
            if (e.target.dataset?.login) {
                this.errorMessageAPI = '';
                this.changeLoginFieldType('input');
            }
        },

        isActive(name) {
            const hasContent = this.form[name] || name === 'notification';
            return hasContent && this.activeForm === name;
        },

        showNotification(data) {
            this.notification = data;
            this.showForm('notification');
        },

        isProsumer() {
            return this.userData?.company_type === 'Prosumer End-Customer';
        },

        isSegmented() {
            if (!this.segmentRules) return false;
            return Object.entries(this.segmentRules).every(([key, value]) => this.userData[key] === value);
        },

        showForm(name) {
            this.isLoading = false;
            this.activeForm = name;
            this.$emit('activeFormName', name);
        },

        hideLogin(email) {
            if (this.userData.email === email) return;
            this.errorMessageAPI = '';
            if (!this.isCloudConsoleLoginFlowEnabled) return;
            if (this.userData.login) {
                this.userData.email = email;
                this.changeLoginFieldType('hidden');
            }
        },

        applyExtraFields(fields) {
            const { fieldsAdd = [], fieldsRemove = [], fieldsChange = [] } = this.form?.registration || {};
            let result = fields;

            if (fieldsRemove.length) {
                const removeSet = new Set(fieldsRemove);
                result = result.filter((f) => !removeSet.has(f.name));
            }

            if (fieldsChange.length) {
                const changeMap = new Map(fieldsChange.filter((f) => f?.name).map((f) => [f.name, f]));
                result = result.map((f) => (changeMap.has(f.name) ? { ...f, ...changeMap.get(f.name) } : f));
            }

            if (fieldsAdd.length) {
                result = [...result];
                fieldsAdd.forEach(({ positionBefore, ...field }) => {
                    const idx = positionBefore ? result.findIndex((f) => f.name === positionBefore) : -1;
                    result.splice(idx !== -1 ? idx : result.length, 0, field);
                });
            }

            return result;
        },

        async changeLoginFieldType(type) {
            if (!this.isCloudConsoleLoginFlowEnabled) return;
            const comparison = (item) => (item.name === 'login' ? { ...item, type } : item);
            this.formRegistrationFields = this.formRegistrationFields.map(comparison);
            this.showForm('');
            this.settingsForm = { validate: false, focus: 'login' };
            this.userData.login = type === 'input' ? this.setLogin() : null;
            await this.$nextTick();
            this.showForm('registration');
        },

        setLoadingMessage() {
            let count = 0;
            const messages = this.form?.registration?.loadingMessages;
            if (!messages) return;

            const myInterval = setInterval(() => {
                ++count;

                const hasMessage = messages.find((item) => item.time > count);
                if (!hasMessage) clearInterval(myInterval);

                const message = messages.find((item) => item.time === count) || {};
                if (message.text) this.loadingMessage = message.text;
            }, 1000);
        },

        setLogin() {
            if (!this.userData.first_name) return '';
            if (!this.userData.last_name) return '';
            return `${this.userData.first_name}.${this.userData.last_name}`.toLowerCase();
        },

        setErrorContent(error) {
            const defaultError = this.formElementsDefault?.errors?.apiErrorCodes?.default || 'Error';
            let title = this.formElementsDefault?.errors?.apiErrorCodes?.[error.code] || defaultError;
            let code = error.code;

            const isAborted = this.isCloudConsoleLoginFlowEnabled && ['ERR_NETWORK', 'ECONNABORTED'].includes(code);
            if (error.code === 'third_party_api_exception' || isAborted) {
                const text = this.formElementsDefault?.errors?.third_party_api_exception?.timeout || {};
                this.errorContent = { ...this.form.error, ...text };
                return;
            }

            if (error.ex && ['invalid_email', 'mailbox_is_disposable_address'].includes(error.code)) {
                this.settingsForm = {
                    focus: 'email',
                };
                const isTypo = error.ex.reason === 'failed custom grammar check' || error.ex.didYouMean;
                const errorKey = isTypo ? 'invalidMailTypo' : 'invalidMailGeneral';
                title = this.formElementsDefault.errors?.eventsMailGunErrors?.[errorKey] || title;
                code = null;
                this.mailgunError = {
                    message: title,
                    email: this.userData.email,
                };

                if (this.isActive('update')) {
                    window.dataLayer = window.dataLayer || [];
                    window.dataLayer.push({
                        event: 'Acronis',
                        eventAction: 'Error',
                        eventCategory: 'Form',
                        eventLabel: error.code,
                    });
                }
            } else {
                this.handleError(error);
            }

            this.errorContent = {
                ...this.form.error,
                title,
                code,
                activeForm: this.isActive('update') ? 'update' : 'registration',
            };
        },

        async isSubmittedUpdate(data, formInstance) {
            this.formInstance = formInstance;

            this.userData = {
                ...data,
                ck: this.ck,
                email: this.accountData?.email || '',
                first_name: this.accountData?.first || '',
                last_name: this.accountData?.last || '',
                country: this.accountData?.country || '',
                state: this.accountData?.state_province || '',
                postal_code: this.accountData?.zip || '',
            };

            if (this.form?.upsell && this.isProsumer()) {
                this.backTo = 'update';
                this.showForm('upsell');
                return;
            }

            if (this.hasRecaptchaInvisible && data.form_id !== 'TRIAL_FORMS_ALL_NO_CAPTCHA') {
                await this.recaptchaActive().execute(this.recaptchaWidgetId);
            }

            try {
                this.isLoading = true;
                if (this.userData.phone !== 'NULL') {
                    await this.accountUpdate({ phone: this.userData.phone });
                }
                await this.accountTrial({ product_id: this.listProductID });
                await this.trySendLead();
            } catch (e) {
                this.sendVwoEvent(EVENT_FORM_ERROR);
                if (e) {
                    this.setErrorContent(e);
                    this.showForm('error');
                }
                return;
            }

            this.isLoading = true;
            const events = this.form?.update?.sendEventsGA || this.settingsFormUpdate?.sendEventsGA || [];
            this.trySendEventsGA(events);
            this.sendVwoEvent(EVENT_FORM_SUCCESS);
            this.$refs.exitBanner?.disableExitBanner();

            const redirectLink = this.settingsFormUpdate?.redirectLink;
            const redirectSegmented = this.settingsFormUpdate?.redirectSegmented;
            if (this.isSegmented() && redirectSegmented) {
                window.location.assign(this.replaceLocale(redirectSegmented));
                return;
            }
            if (redirectLink) window.location.assign(this.replaceLocale(redirectLink));
        },

        async getUserDataDB() {
            if (this.accountData) return {};
            const db = await getDB();
            const last = await db.fields.orderBy('id').last();
            const result = await db.fields.get(last?.id || '');
            const fields = Object.assign({}, ...(result || [{}]));
            delete fields.event_time;
            return fields;
        },

        async getUserData() {
            if (this.$store.state.user) {
                // Not all Sites have `user` module included in the Store.
                await this.$store.dispatch('user/get');
            }

            if (this.accountData && this.form.login && this.settingsFormUpdate) {
                if (this.form.flow.doNotShowFormUpdate) {
                    await this.isSubmittedUpdate({
                        phone: 'NULL',
                        industry: 'NULL',
                        of_Employees__c: 'NULL',
                        form_id: 'TRIAL_FORMS_ALL_NO_CAPTCHA',
                    });
                    return;
                }
                if (this.activeForm === 'login') {
                    this.showForm('update');
                    return;
                }
            }

            if (this.accountData?.country) {
                const hasIsoCode = this.countriesData.find((item) => item.iso_code === this.accountData.country);
                if (!hasIsoCode) this.accountData.country = '';
            }

            this.userData = await this.getUserDataDB();
            this.sanitizeUserDataForms();
            this.sanitizeUserDataOptions();
            this.showForm(this.activeForm || 'registration');
        },

        sanitizeUserDataOptions() {
            this.formFields.forEach((field) => {
                if (!field.options?.length) return;

                const value = this.userData[field.name];
                if (!value) return;

                const options = field.options || [];

                if (Array.isArray(value)) {
                    const validValues = new Set(options.map((item) => item.value));
                    const filtered = value.filter((v) => validValues.has(v));
                    if (filtered.length) {
                        this.userData[field.name] = filtered;
                    } else {
                        delete this.userData[field.name];
                    }
                    return;
                }

                const isValid = options.some((item) => item.value === value);
                if (isValid) return;

                delete this.userData[field.name];
            });
        },

        sanitizeUserDataForms() {
            const steps = this.form.registration?.steps || [];

            steps.forEach((step, stepIndex) => {
                if (!step.hasDifferentForms) return;

                const activeOption = this.getFormOptions(stepIndex)
                    .find((option) => option.value === this.userData[step.checkField]);
                if (!activeOption) return;

                this.clearInactiveFormFields(activeOption.nextStep ?? stepIndex + 1, stepIndex);
            });
        },

        async setUserData() {
            const fields = this.formFields;
            if (!fields.length) return;
            const userData = fields
                .filter((item) => !['password', 'textarea'].includes(item.type) && item.name !== 'event_time')
                .map((item) => {
                    const raw = this.userData?.[item.name];
                    // Check handles multi-select fields (isMultiple: true)
                    const value = Array.isArray(raw) ? [...raw] : (raw || '');
                    return value && value.length ? { [item.name]: value } : '';
                })
                .filter(Boolean);

            const db = await getDB();

            const last = await db.fields.orderBy('id').last();
            if (last?.id) {
                const userDataSaved = await db.fields.get(last.id);
                const userDataMerged = {
                    ...Object.assign({}, ...userDataSaved),
                    ...Object.assign({}, ...userData),
                };
                const userDataUpdated = Object.keys(userDataMerged).map((key) => ({ [key]: userDataMerged[key] }));
                await db.fields.update(last.id, userDataUpdated);
                return;
            }
            await db.fields.add(userData);
        },

        async init() {
            await this.getGeoLocation();

            twoFa.cleanupExpired();

            const country = this.location?.data?.country?.code || '';
            const ret = twoFa.consumeReturn();
            if (ret) {
                if (this.hasRecaptchaInvisible) {
                    await this.recaptchaInject(country);
                }
                await this.handle2FAReturn(ret);
                return;
            }

            // WEB-51974
            const notifications = this.formElementsDefault?.notification;
            if (Array.isArray(notifications)) {
                const currentUrl = window.location.href;
                const siteID = this.$store.state.context?.public?.siteID || '';
                const matchedGroup = notifications.find((group) =>
                    group.forms?.some((item) => currentUrl.includes(item.path) || siteID === item.path),
                );
                const matchedCountry = matchedGroup?.countries?.find((item) => item.iso === country);
                if (matchedCountry) {
                    this.notification = matchedCountry;
                    this.showForm('notification');
                    return;
                }
            }

            if (this.hasRecaptchaInvisible) {
                this.recaptchaInject(country);
            }

            await this.getUserData();
        },

        async handle2FAReturn({ outcome, context }) {
            if (this.formHasSteps && Number.isFinite(context.formStep)) {
                this.formStep = context.formStep;
                // trial-form.vue picks the step's preset from its own `step`; sync it before reading `this.form`.
                this.$emit('changeStep', context.formStep);
                await this.$nextTick();
            }
            this.userData = { ...this.userData, ...(context.userData || {}) };

            const codes = this.formElementsDefault?.errors?.apiErrorCodes || {};

            // Account also sends users to cancel_url on an expired token or an OTP lockout, without a reason,
            // so the copy must not assume the user cancelled.
            if (outcome === 'cancel') {
                this.errorContent = {
                    ...this.form.error,
                    title: codes.two_fa_cancelled_title || 'We couldn\'t complete two-factor authentication',
                    description: codes.two_fa_cancelled_description || 'Please log in again.',
                    code: null,
                    activeForm: context.activeForm || 'registration',
                };
                this.showForm('error');
                return;
            }

            try {
                if (this.$store.state.user) await this.$store.dispatch('user/get');
            } catch (e) { /* fall through — accountData check below decides */ }

            if (!this.accountData) {
                this.errorContent = {
                    ...this.form.error,
                    title: codes.two_fa_session_missing_title || 'We could not verify your two-factor authentication',
                    description: codes.two_fa_session_missing_description || 'Please try logging in again.',
                    code: null,
                    activeForm: context.activeForm || 'login',
                };
                this.showForm('error');
                return;
            }

            if (context.activeForm === 'login') {
                // Restore so getUserData()'s `activeForm === 'login'` branch routes to the update form.
                this.activeForm = 'login';
                await this.getUserData();
                return;
            }

            this.session2FAResolved = true;
            try {
                await this.submit();
            } finally {
                this.session2FAResolved = false;
            }
        },

        isSubmittedRegistration(data, formInstance, formEventContext) {
            this.formEventContext = formEventContext;
            this.formInstance = formInstance;

            this.userData = {
                ...data,
                ck: this.ck,
            };

            if (this.skipSendLead) {
                this.submit();
                return;
            }

            if (this.formScheduleContent) {
                this.showForm('schedule');
                this.submit();
                return;
            }

            if (this.isProsumer()) {
                if (this.form?.upsell) {
                    this.backTo = 'registration';
                    if (this.form?.upsell?.sendEventsGA) {
                        this.trySendEventsGA(this.form.upsell.sendEventsGA);
                    }
                    this.showForm('upsell');
                    return;
                }
                if (this.form?.support) {
                    this.showForm('support');
                    return;
                }
            }

            if (this.userData?.company_type) {
                const fields = this.form?.registration?.fields || [];
                const options = fields.find((item) => item.name === 'company_type')?.options || [];
                const value = this.userData.company_type;
                const option = utils.findOptionByValue(options, value) || {};
                this.redirectLink = option?.redirectLink || this.form?.flow?.redirectLink || '';
                this.redirectSegmented = option?.redirectSegmented || this.form?.flow?.redirectSegmented || '';
            }

            if (this.isResourceCenter) {
                this.userData.terms = true;
            }

            this.submit();
        },

        isSubmittedUpsell() {
            this.userData = {
                ...this.userData,
                company: '',
                industry: '',
                company_type: 'Corporate End-Customer',
            };
            if (this.isContactsRenewals) {
                this.userData.company_type = 'Home Products';
            }
            if ([47424, 47425].includes(this.form.version)) {
                this.userData.company_type = 'Corporate End-Customer';
                this.changeStep(1);
                return;
            }

            this.settingsForm = {
                validate: true,
                focus: 'company',
            };

            this.showForm(this.backTo);
        },

        trySendEventsGA(events) {
            events.forEach(async (item) => {
                // WEB-44753, WEB-45411
                const event = mapValues(item, (value) => {
                    if (value === 'setCountry') {
                        return this.userData?.country || '';
                    }
                    if (value === 'setState') {
                        return this.userData?.state || '';
                    }
                    if (value === 'setDataCenter') {
                        return this.userData?.dc_location || '';
                    }
                    if (value === 'setCompanyType') {
                        return this.userData?.company_type || '';
                    }
                    if (value === 'setServiceSelector') {
                        const conf = this.formRegistrationContent?.fields?.find((f) => f.name === 'service-selector');
                        const selectorValue = this.userData?.['service-selector'];
                        const opt = utils.findOptionByValue(conf?.options, selectorValue);
                        return opt?.label || '';
                    }
                    if (value === 'hasLoginField') {
                        return this.userData?.login ? 'user login' : 'no user login';
                    }
                    if (value === 'setDeveloperPreference') {
                        return this.userData?.["I'd like"] || '';
                    }
                    if (value === 'setNumOfEmployees') {
                        return this.userData?.['of_Employees__c'] || '';
                    }
                    if (isString(value) && value.startsWith('userData:')) {
                        const fieldName = value.slice('userData:'.length);
                        const fieldValue = this.userData?.[fieldName] || '';
                        if (fieldValue === 'booleanTrue') return true;
                        if (fieldValue === 'booleanFalse') return false;
                        return fieldValue;
                    }
                    return value;
                });

                if (event.eventAction === 'submit_form_success') {
                    if (!event.emailAddress) {
                        event.emailAddress = await commonUtils.toSHA256String(this.userData?.email || ''); // WEB-45304
                    }
                    event.formTime = utils.getSecondsOnPage(); // WEB-44946
                }

                this.sendDataLayer({
                    eventContext: this.formEventContext || '',
                    eventContent: this.isFreeEmail(this.userData?.email || '') ? 'FreeEmail' : 'BusinessEmail',
                    ...event,
                });
            });
        },

        async tryRegisterBeta() {
            if (!get(this, 'form.isBeta', false)) {
                return true;
            }

            if (this.session2FAResolved) {
                return true;
            }

            try {
                this.isLoading = true;
                await this.$store.dispatch('user/logout');
                const response = await this.accountLogin({
                    email: this.userData.email,
                    password: this.userData.password,
                });

                if (twoFa.requires2FASetupFirst(response)) {
                    this.errorMessageAPI = twoFa.setupFirstMessage(this.formElementsDefault?.errors?.apiErrorCodes?.two_fa_setup_required);
                    return Promise.reject();
                }

                if (twoFa.requires2FA(response)) {
                    twoFa.redirectTo2FA(response, {
                        activeForm: 'registration',
                        formStep: this.formStep,
                        userData: this.userData,
                    });
                    return Promise.reject();
                }

                if (twoFa.isOtpLocked(response)) {
                    this.errorMessageAPI = this.formElementsDefault?.errors?.apiErrorCodes?.otp_locked ||
                        'Too many attempts. Please try again later.';
                    return Promise.reject();
                }

                if (response.status === 200) {
                    return true;
                }

                const message = this?.form?.registration?.messages?.error || '';
                this.errorMessageAPI = message || 'Wrong email or password';
                return Promise.reject();
            } catch (e) {
                this.setErrorContent(e);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        async tryRegisterAccount() {
            if (!this.needToRegisterAccount) return true;

            try {
                this.isLoading = true;

                const response = await this.accountRegister(this.payloadAccount);

                if (response.data?.errors?.email) {
                    const message = this.form?.registration?.messages?.accountExist || '';
                    this.errorMessageAPI = message || 'This email address is already in use';
                    this.settingsForm = { focus: 'email' };
                    const step = this.form.registration?.steps?.findIndex((item) => item?.fields?.find((field) => field?.name === 'email'));
                    this.changeStep(this.form.registration?.step || step || 0);
                    return Promise.reject();
                }

                if (response.status !== 204) {
                    throw new Error('Unsuccessful');
                }
            } catch (e) {
                this.setErrorContent(e);
                this.showForm('error');
                return Promise.reject();
            }

            await this.accountLogin({
                email: this.userData.email,
                password: this.userData.password,
            });

            await this.accountAgreement({ accept: true });

            await this.accountTrial({ product_id: this.listProductID });

            this.isLoading = false;
            return true;
        },

        async mailgunValidation() {
            if (this.session2FAResolved) return true;
            if (!this.needToRegisterAccount) return true;
            if (this.formVariation.skipMailgunValidation) return true;

            await this.executeRecaptcha();

            try {
                this.isLoading = true;
                const payload = this.payloadMarketo();
                const isEmailValid = await this.isEmailValid({
                    email: payload.email,
                    form_id: payload.form_id,
                    // eslint-disable-next-line no-underscore-dangle
                    recaptchaResponse: payload._recaptchaResponse,
                });
                return isEmailValid;
            } catch (error) {
                this.setErrorContent(error);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        async trySendLead() {
            if (!this.hasFormID) return true;
            try {
                this.isLoading = true;
                this.setLoadingMessage();

                const payload = {
                    ...this.payloadMarketo(),
                    actionURL: this.sendLeadTo || '/svc/v1/marketing/forms/leads',
                };

                if (this.isCloudConsoleLoginFlowEnabled) {
                    payload.login = payload.login || null;
                }

                if (this.formRegistrationContent.isFormEvents) {
                    // WEB-46729
                    payload.attend_training = payload.attend_training === 'true';
                }

                if (this.form.flow?.sendDowntimeCalculatorLink) {
                    const downtimeLink = localStorage.getItem('downtimeCalculatorLink');
                    if (downtimeLink) {
                        payload.downtime_calculator_link = downtimeLink;
                    }
                }

                const start = new Date();
                this.response = await this.sendLead(payload);

                if (this.form.flow?.personalizationLocalStorage) {
                    localStorage.setItem(this.form.flow.personalizationLocalStorage, 'true');
                }

                if (this.form.flow?.sendDowntimeCalculatorLink) {
                    localStorage.removeItem('downtimeCalculatorLink');
                }

                if (this.isCloudConsoleLoginFlowEnabled) {
                    const end = new Date();
                    const seconds = (end.getTime() - start.getTime()) / 1000;
                    const consoleURL = this.response?.data?.gatewayBody?.console_url;
                    if (consoleURL && seconds <= 30) this.redirectLink = consoleURL;
                }

                return true;
            } catch (error) {
                if (error.code === 'third_party_api_exception') {
                    const gatewayStatus = error.ex?.gatewayStatus;
                    const exception = this.formElementsDefault?.errors?.third_party_api_exception || {};

                    if (gatewayStatus === 409 && this.userData.login) {
                        this.settingsForm = {
                            focus: 'login',
                            validate: true,
                            errorMessageLogin: exception.login || 'Login is already in use. Specify another login different from email',
                        };
                        this.changeStep(this.form.registration?.step || 0);
                        return Promise.reject();
                    }

                    const message = exception[gatewayStatus] || exception?.unknown || 'Error';
                    if (isString(message)) {
                        this.errorMessageAPI = message.replace('@userEmail', this.userData.email);
                        this.settingsForm = { validate: true, focus: 'email' };
                        this.changeStep(this.form.registration?.step || 0);
                        return Promise.reject();
                    }

                    this.errorContent = { ...this.form.error, ...message };
                    this.showForm('error');
                    return Promise.reject();
                }
                this.setErrorContent(error);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        async trySendContactsRenewals() {
            if (!this.isContactsRenewals) return true;
            try {
                this.isLoading = true;
                const payload = this.payloadMarketo();
                const isHomeProducts = payload.company_type === 'Home Products';
                const companyName = isHomeProducts ? 'Home' : payload.companyName;
                await this.sendLead({
                    ...payload,
                    Origin: 'web',
                    termsAgreement: true,
                    countryCode: payload.country,
                    contactEmail: payload.email,
                    contactPhone: payload.phone,
                    Subject: `${companyName} - Renewal Request`,
                    companyName,
                    licenseInvoice: isHomeProducts ? 'Home' : payload.licenseInvoice,
                    productRegistrationEmail: isHomeProducts ? payload.email : payload.productRegistrationEmail,
                    actionURL: isHomeProducts
                        ? '/svc/v1/acronis-contacts/renewals-home-office/case'
                        : '/svc/v1/acronis-contacts/renewals/case',
                });
                return true;
            } catch (e) {
                this.setErrorContent(e);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        async trySendContactUsForm() {
            if (!this.form.flow.isContactUsForm) {
                return true;
            }
            try {
                this.isLoading = true;
                const payload = this.payloadMarketo();
                await this.sendContactUsForm(payload);
                return true;
            } catch (e) {
                this.setErrorContent(e);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        async trySendEventSubscription() {
            try {
                this.isLoading = true;

                const payload = {
                    ...this.payloadMarketo(),
                    form_id: this.form.success.leadOnSuccessCTA.form_id,
                    actionURL: this.sendLeadTo || '/svc/v1/marketing/forms/leads',
                    opt_in_status: true,
                };
                payload.attend_training = payload.attend_training === 'true';

                await this.sendLead(payload);
                this.showForm('');
                this.$emit('onClickButtonSuccess');
                await this.$nextTick();
                this.showForm('success');
            } catch (e) {
                this.setErrorContent(e);
                this.showForm('error');
                return Promise.reject();
            } finally {
                this.isLoading = false;
            }
        },

        onSubmitError() {
            if (this.hasAnimate) {
                this.isShaking = true;
                setTimeout(() => { this.isShaking = false; }, 333);
            }
            this.sendVwoEvent(EVENT_FORM_ERROR);
        },

        async changeStep(step, replaceExtraButtons = null) {
            if (step !== this.formStep) {
                this.previousFormStep = this.formStep;
            }
            this.clearInactiveFormFields(step);
            this.formStep = step;
            this.replaceExtraButtons = replaceExtraButtons;
            this.showForm('');
            this.$emit('changeStep', step);
            await this.$nextTick();
            this.formRegistrationFields = this.registrationFields;
            this.showForm('registration');
            this.$refs.container.closest('.s-form')?.scrollIntoView({ behavior: 'smooth' });
        },
        getFormOptions(stepIndex) {
            const step = this.form.registration?.steps?.[stepIndex] || {};
            const formField = (step.fields || []).find((field) => field.name === step.checkField);
            return formField?.options || formField?.list || [];
        },
        clearInactiveFormFields(activeStep, sourceStep = this.formStep) {
            const steps = this.form.registration?.steps || [];
            if (!steps[sourceStep]?.hasDifferentForms) return;

            // An option without `nextStep` falls through to the next step, same as in `submit`
            const differentFormSteps = [...new Set(this.getFormOptions(sourceStep)
                .map((option) => option.nextStep ?? sourceStep + 1)
                .filter(Number.isInteger))];
            if (differentFormSteps.length < 2 || !differentFormSteps.includes(activeStep)) return;

            const inactiveSteps = differentFormSteps.filter((step) => step !== activeStep);
            // Keep whatever is used outside the inactive forms: shared fields may already be filled
            const keepNames = new Set(steps
                .flatMap((step, index) => (inactiveSteps.includes(index) ? [] : step.fields || []))
                .map((field) => field.name));

            inactiveSteps
                .flatMap((step) => steps[step]?.fields || [])
                .filter((field) => !keepNames.has(field.name))
                .forEach((field) => delete this.userData[field.name]);
        },
        async trySetUserData() {
            try {
                await this.setUserData();
            } catch (e) {
                // eslint-disable-next-line no-console
                console.warn('setUserData failed:', e);
            }
        },
        async executeRecaptcha() {
            if (!this.hasRecaptchaInvisible) return;
            this.isLoading = true;
            try {
                this.recaptchaResponse = await this.recaptchaActive().execute(this.recaptchaWidgetId);
            } finally {
                this.isLoading = false;
            }
        },
        async submit() {
            this.guid = utils.getGUID();

            if (this.skipSendLead) {
                await this.trySetUserData();
                await this.executeRecaptcha();
                this.$emit('submit', {
                    userData: this.userData,
                    response: null,
                    recaptchaResponse: this.recaptchaResponse,
                });
                return;
            }

            try {
                await this.mailgunValidation();
                await this.executeRecaptcha();
                if (this.formHasSteps) {
                    if (this.formStep < this.form.registration.steps.length - 1) {
                        const field = this.formVariation?.checkField || '';
                        const value = this.userData[field];
                        if (field && !value) return;
                        const matchedOption = this.getFormOptions(this.formStep).find((i) => i.value === value);
                        const item = matchedOption || {};
                        const eventsGA = item.sendEventsGA || this.formVariation.sendEventsGA || [];
                        this.trySendEventsGA(eventsGA);
                        if (item.notification) {
                            this.showNotification(item.notification);
                            return;
                        }
                        if (item.redirectLink) {
                            this.isLoading = true;
                            window.location.assign(item.redirectLink);
                            return;
                        }
                        const skipRemainingSteps = Boolean(matchedOption?.submitOnSelect);
                        if (!skipRemainingSteps && !this.formVariation.lastStep) {
                            this.changeStep(item.nextStep ?? (this.formStep + 1), item.replaceExtraButtons);
                            return;
                        }
                    }
                }
                await this.trySendContactUsForm();
                await this.tryRegisterAccount();
                await this.tryRegisterBeta();
                await this.trySendLead();
                await this.trySendContactsRenewals();
            } catch (e) {
                this.sendVwoEvent(EVENT_FORM_ERROR);
                return;
            }

            await this.trySetUserData();

            this.isLoading = true;
            this.sendEventMarketingSubscription(this.userData.gdpr_opt_in || this.userData.opt_in);
            this.trySendEventsGA(this.finalEventsGA);
            this.sendVwoEvent(EVENT_FORM_SUCCESS);
            this.$refs.exitBanner?.disableExitBanner();

            if (this.formScheduleContent) {
                this.isLoading = false;
                return;
            }

            if (this.submitDelay) {
                await sleep(this.submitDelay);
            }

            if (this.isSegmented() && this.redirectSegmented) {
                window.location.assign(this.replaceLocale(this.redirectSegmented));
                return;
            }

            if (this.redirectLink) {
                window.location.assign(this.replaceLocale(this.redirectLink));
                return;
            }

            if (this.form?.flow?.redirectToCleverbridge) {
                this.redirectToCleverbridge(this.payloadPartnerSignupCloud);
                return;
            }

            if (this.form?.flow?.getExpressSignupFormSURL) {
                this.redirectToCleverbridge(this.payloadExpressSignup);
                return;
            }

            if (this.hasSuccessPage) {
                this.showForm('success');
            }

            if (this.hasNotification) {
                this.notification = this.form?.flow.notification;
                this.showForm('notification');
            }

            this.$emit('submit', {
                userData: this.userData,
                response: this.response,
            });
        },

        sendVwoEvent(type) {
            if (typeof window === 'undefined') return;
            if (!this.formInstance) return;
            const eventValue = type === EVENT_FORM_SUCCESS ? 1 : 0;
            window.VWO = window.VWO || [];
            window.VWO.push(['nls.formAnalysis.markSuccess', this.formInstance, eventValue]);
            delete this.formInstance;
        },

        async onClickButtonSuccess() {
            if (this.form?.success?.leadOnSuccessCTA) {
                await this.trySendEventSubscription();
                return;
            }
            this.$emit('onClickButtonSuccess');
        },
    },
};
</script>

<style lang="pcss" scoped>
.s-form-container {
    position: relative;
    margin: 40px -16px auto;
    color: var(--av-fixed-secondary);

    @media (--viewport-mobile-wide) {
        width: 100%;
        margin: 56px auto auto;
    }

    @media (--viewport-tablet) {
        width: 648px;
    }

    @media (--viewport-desktop) {
        margin: 0 auto auto;
        width: 552px;
    }

    @media (--viewport-desktop-wide) {
        width: 648px;
    }

    .s-form-title {
        @mixin display;
        text-align: center;
        margin-bottom: 32px;
        color: var(--av-nav-primary);
        :deep(.a-link) {
            .a-link__content {
                @mixin display;
                color: var(--av-nav-primary);
            }
        }
    }

    .s-form-content {
        position: relative;
    }

    &__loading {
        top: 2px;
        inset-inline-start: 2px;
        gap: 16px;
        z-index: 10;
        display: flex;
        padding: 0 48px;
        min-height: 128px;
        text-align: center;
        position: absolute;
        border-radius: 4px;
        align-items: center;
        flex-direction: column;
        justify-content: center;
        width: calc(100% - 4px);
        height: calc(100% - 4px);
        color: var(--av-fixed-primary);
        background: var(--av-inversed-secondary);

        &.with-logo {
            @media (--viewport-desktop) {
                top: 58px;
                height: calc(100% - 60px);
            }
        }
    }

    :deep(.el-form) {
        &-item {
            padding: 0;
        }
    }

    :deep(.el-input) {
        &__container {
            text-align: start;
        }

        &__placeholder {
            @mixin paragraph;
        }

        &__label {
            @mixin paragraph;

            &.is-active {
                @mixin caption;
                padding-top: 4px;
            }
        }
    }

    :deep(.el-textarea) {
        &__editor {
            @mixin body;
            max-height: 134px;
        }
    }

    :deep(.el-checkbox) {
        @mixin body;
        display: block;
        text-align: start;
        color: var(--av-fixed-secondary);

        @media (--viewport-tablet) {
            @mixin paragraph;
        }

        &:not(:first-child) {
            margin-top: 8px;
        }
    }

    :deep(input),
    :deep(select),
    :deep(textarea) {
        @mixin paragraph;
    }

    .g-recaptcha {
        :deep(> div) {
            margin: 0 auto;
        }

        :deep(.grecaptcha-badge) {
            display: none;
        }
    }
}

.s-form-container.s-form-container-version-42019 {
    margin: 40px 0 auto;

    @media (--viewport-tablet) {
        width: 50%;
        margin-top: 0;
    }

    @media (--viewport-desktop) {
        max-width: 472px;
    }

    @media (--viewport-desktop-wide) {
        max-width: 568px;
    }

    @media (--viewport-desktop-large) {
        max-width: 648px;
    }
}
.s-form-container.s-form-container-version-47424,
.s-form-container.s-form-container-version-47425 {
    :deep(.s-form-upsell-container) {
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 10;
        display: flex;
        position: fixed;
        align-items: center;
        justify-content: center;
        &:before {
            top: 0;
            left: 0;
            content: '';
            width: 100%;
            height: 100%;
            position: fixed;
            background: rgba(0, 32, 77, 0.65);
        }
        .s-form-upsell {
            padding: 40px;
            max-width: 568px;
            border-radius: 4px;
            border: 1px solid #e3ecf9;
            background: var(--av-inversed-primary);
            box-shadow: 0 4px 8px 0 rgba(36, 49, 67, 0.10);
            .s-form-upsell-title {
                @mixin nav-title-accent;
                margin: 0 auto;
                max-width: 424px;
                color: var(--av-nav-primary);
            }
            .s-form-upsell-image {
                width: auto;
                height: 194px;
            }
            .s-form-upsell-description {
                @mixin paragraph;
            }
            .s-form-upsell-button {
                @mixin paragraph-accent;
                padding: 12px 16px;
                color: var(--av-inversed-primary);
                background: var(--av-button-main);
                &:hover {
                    background: var(--av-button-main-hover);
                }
            }
            .s-form-upsell-link {
                @mixin paragraph;
                margin-top: 24px;
                color: var(--av-link-primary);
                &:hover {
                    color: var(--av-link-primary-hover);
                }
            }
        }
    }
}

.s-form-container.s-form-container-version-47424 {
    :deep(.s-form-upsell-container) {
        top: 16px;
        display: flex;
        overflow: scroll;
        align-items: flex-start;
        max-height: calc(100% - 32px);
        @media (--viewport-tablet) {
            align-items: center;
        }
    }
}
</style>

<style lang="pcss">
.el-select-dropdown {
    &.el-select-dropdown__form-registration {
        .el-text--strong {
            font-weight: 500;
        }
        .el-select-group {
            .el-select-dropdown__item {
                height: auto;
                padding: 0 16px;

                &.is-multi-line {
                    padding: 8px 16px;
                }
            }
        }
        .el-select-group__title {
            @mixin note-heading;
            padding: 8px 16px;
            color: var(--av-fixed-light);
        }
        .el-select-group__wrap:not(:last-of-type) {
            padding-bottom: 16px;

            &::after {
                bottom: 8px;
                background: var(--av-brand-accent);
            }
        }
    }
}

@keyframes s-form-shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-6px); }
    40%, 80% { transform: translateX(6px); }
}

.s-form-container--shake {
    animation: s-form-shake 0.333s ease-in-out;
}
</style>
